import {
  useState, useCallback, useEffect, useRef,
} from 'react'
import { useApolloClient } from '@apollo/client'
import { OracleAgentChatInput, OracleCardDocument, TarotDocument } from '../../../graphql-types'
import {
  useOracleAgentChatSubscriptionSubscription,
  GetChatMessagesDocument,
  GetHistoryDocument,
  OracleAgentChatSubscriptionSubscription,
  GetChatMessagesQuery,
} from '../queries.generated'
import { SubRateLimitState } from '../rateLimitUtils'
import { createOracleSubscriptionInput, CHAT_MODE } from '../oracleChatSessionConfig'
import { RelatedDocument } from '../MediaTile'
import { useModerationError, ModerationError, ModerationSeverity } from '../../OracleCommons'

/**
 * Picks the best asset URL by language preference:
 * exact match > English fallback > first available.
 */
type AssetRef = {
  asset?: { renderUrl?: string | null; language?: string | null } | null
} | null
const pickAssetByLanguage = (
  assets: Array<AssetRef> | null | undefined,
  language: string,
): string | undefined => {
  if (!assets || assets.length === 0) return undefined
  const match = assets.find((a) => a?.asset?.language === language)
    || assets.find((a) => a?.asset?.language === 'en')
    || assets[0]
  return match?.asset?.renderUrl ?? undefined
}

/**
 * Picks the best media language: exact match > English fallback > first available.
 */
const pickMediaLanguage = (
  mediaAssets: Array<{ language?: string | null } | null> | null | undefined,
  language: string,
): string | null => {
  if (!mediaAssets || mediaAssets.length === 0) return null
  const match = mediaAssets.find((a) => a?.language === language)
    || mediaAssets.find((a) => a?.language === 'en')
    || mediaAssets[0]
  return match?.language ?? null
}

/**
 * Type utilities for deriving types from generated GraphQL subscription
 */

// Extract the nested payload type from the subscription
type OracleAgentChatPayload = OracleAgentChatSubscriptionSubscription['oracleAgentChat']

// Extract the documents array type from the payload
export type SubscriptionDocuments = NonNullable<OracleAgentChatPayload['documents']>

// Extract a single document type from the array
type SubscriptionDocument = SubscriptionDocuments[number]

// Extract the cache documents type from the GetChatMessages query
type CacheMessagePair = GetChatMessagesQuery['oracleMessagePairList']['oracleMessagePairs'][number]
export type CacheDocuments = CacheMessagePair['documents']
type CacheDocument = CacheDocuments[number]

/**
 * Extended document type that includes content link fields for inline rendering.
 * Backward-compatible with RelatedDocument — components that only need tile data
 * can use it without changes.
 */
export interface EnrichedDocument extends RelatedDocument {
  componentId?: string
  componentType?: string
  contentLinkPath?: string
  oracle?: OracleCardDocument | null
  tarot?: TarotDocument | null
}

/**
 * Transforms subscription documents to the cache format expected by GetChatMessages query.
 *
 * IMPORTANT: The `content` object below must include every field selected on
 * `Content` in the GetChatMessages and OracleAgentChatSubscription queries
 * (see queries.graphql). Apollo will log a cache inconsistency warning for
 * any field present in the query but missing here.
 */
export function transformSubscriptionDocumentsForCache (
  documents: SubscriptionDocuments | null,
): CacheDocuments {
  if (!documents || documents.length === 0) {
    return []
  }

  return documents.map((doc: SubscriptionDocument): CacheDocument => {
    // documentsId and content.id should have the same value
    // Priority: content.id (string) > String(content.contentId) > String(contentId)
    const documentId = doc.content?.id
      || (doc.content?.contentId ? String(doc.content.contentId) : null)
      || String(doc.contentId)

    return {
      __typename: 'OracleDocument',
      documentsId: documentId,
      componentId: doc.componentId,
      componentType: doc.componentType,
      contentType: doc.contentType,
      title: doc.title,
      contentLink: doc.contentLink || null,
      oracle: doc.oracle || null,
      tarot: doc.tarot || null,
      content: {
        __typename: 'Content',
        id: documentId,
        contentId: doc.content?.contentId || null,
        path: doc.content?.path || doc.path,
        title: doc.content?.title || null,
        contentType: doc.content?.contentType || null,
        contentAssetReferences: doc.content?.contentAssetReferences || null,
        featureMedia: doc.content?.featureMedia || null,
        contentSeries: doc.content?.contentSeries || null,
      },
    }
  })
}

/**
 * Transforms cache documents to UI-friendly EnrichedDocument format.
 * Extracts the thumbnail URL from the nested content.contentAssetReferences structure.
 * Content link documents pass through even without thumbnails (they render inline, not as tiles).
 *
 * @param documents - Documents from Apollo cache (GetChatMessages query)
 * @returns Array of EnrichedDocument objects ready for UI display
 */
export function transformCacheDocumentsForUI (
  documents: CacheDocuments | null,
  language: string,
): EnrichedDocument[] {
  if (!documents || documents.length === 0) {
    return []
  }

  const result = documents
    .map((doc: CacheDocument) => {
      const renderUrl = pickAssetByLanguage(doc.content?.contentAssetReferences, language)
      const path = doc.content?.path
      const contentId = doc.content?.contentId
      const mediaId = parseInt(doc.content?.featureMedia?.mediaId, 10) || null

      // Content link documents don't need thumbnails — they render as inline links
      if (doc.componentType === 'contentLink' && doc.contentLink) {
        return {
          contentId: doc.contentLink.contentId || contentId || 0,
          thumb: renderUrl || '',
          title: doc.contentLink.title || doc.content?.title || '',
          path: doc.contentLink.path || doc.content?.path || '',
          contentType: doc.contentLink.contentType || doc.content?.contentType,
          parentSeriesName: doc.content?.contentSeries?.[0]?.title || null,
          parentSeriesId: doc.content?.contentSeries?.[0]?.contentId || null,
          mediaId,
          mediaLanguage: pickMediaLanguage(doc.content?.featureMedia?.mediaAssets, language),
          componentId: doc.componentId,
          componentType: doc.componentType,
          contentLinkPath: doc.contentLink.path,
        }
      }

      // Oracle card documents don't need thumbnails — they render as card spreads
      if (doc.oracle) {
        return {
          contentId: contentId || 0,
          thumb: '',
          title: doc.content?.title || '',
          path: doc.content?.path || '',
          contentType: doc.content?.contentType,
          componentId: doc.componentId,
          componentType: doc.componentType,
          oracle: doc.oracle,
        }
      }

      // Tarot card documents — rendered inline as JSON (v1, no polished UI)
      if (doc.tarot) {
        return {
          contentId: contentId || 0,
          thumb: '',
          title: doc.content?.title || '',
          path: doc.content?.path || '',
          contentType: doc.content?.contentType,
          componentId: doc.componentId,
          componentType: doc.componentType,
          tarot: doc.tarot,
        }
      }

      // Skip non-contentLink documents without a valid thumbnail or path or contentId
      if (!renderUrl || !path || !contentId) {
        return null
      }

      // documentsId should be a number string
      if (Number.isNaN(contentId)) {
        return null
      }

      return {
        contentId,
        thumb: renderUrl,
        title: doc.content?.title || '',
        path: doc.content?.path || '',
        contentType: doc.content?.contentType,
        parentSeriesName: doc.content?.contentSeries?.[0]?.title || null,
        parentSeriesId: doc.content?.contentSeries?.[0]?.contentId || null,
        mediaId,
        mediaLanguage: pickMediaLanguage(doc.content?.featureMedia?.mediaAssets, language),
        componentId: doc.componentId,
        componentType: doc.componentType,
      }
    })
    .filter((doc): doc is NonNullable<typeof doc> => doc !== null)

  return result
}

/**
 * Transforms GraphQL subscription documents to UI-friendly EnrichedDocument format.
 * Extracts the thumbnail URL from the nested content.contentAssetReferences structure.
 * Content link documents pass through even without thumbnails (they render inline, not as tiles).
 *
 * @param documents - Documents from the GraphQL subscription
 * @returns Array of EnrichedDocument objects ready for UI display
 */
export function transformSubscriptionDocumentsForUI (
  documents: SubscriptionDocuments | null,
  language: string,
): EnrichedDocument[] {
  if (!documents || documents.length === 0) {
    return []
  }

  return documents
    .map((doc: SubscriptionDocument) => {
      // Extract thumbnail URL from content asset references, preferring the user's language
      const renderUrl = pickAssetByLanguage(doc.content?.contentAssetReferences, language)

      let mediaId: number | null = null
      if (doc.content?.featureMedia?.mediaId) {
        const rawMediaId = doc.content.featureMedia.mediaId
        mediaId = typeof rawMediaId === 'string' ? parseInt(rawMediaId, 10) : rawMediaId
        if (Number.isNaN(mediaId)) {
          mediaId = null
        }
      }

      // Content link documents don't need thumbnails — they render as inline links
      if (doc.componentType === 'contentLink' && doc.contentLink) {
        return {
          contentId: doc.contentLink.contentId || doc.contentId,
          thumb: renderUrl || '',
          title: doc.contentLink.title || doc.title,
          path: doc.contentLink.path || doc.path,
          contentType: doc.contentLink.contentType || doc.contentType,
          parentSeriesName: doc.content?.contentSeries?.[0]?.title || null,
          parentSeriesId: doc.content?.contentSeries?.[0]?.contentId || null,
          mediaId,
          mediaLanguage: pickMediaLanguage(doc.content?.featureMedia?.mediaAssets, language),
          isFeaturedMedia: !!doc.content?.featureMedia,
          componentId: doc.componentId,
          componentType: doc.componentType,
          contentLinkPath: doc.contentLink.path,
        }
      }

      // Oracle card documents don't need thumbnails — they render as card spreads
      if (doc.oracle) {
        return {
          contentId: doc.contentId,
          thumb: '',
          title: doc.title,
          path: doc.path,
          contentType: doc.contentType,
          componentId: doc.componentId,
          componentType: doc.componentType,
          oracle: doc.oracle,
        }
      }

      // Tarot card documents — rendered inline as JSON (v1, no polished UI)
      if (doc.tarot) {
        return {
          contentId: doc.contentId,
          thumb: '',
          title: doc.title,
          path: doc.path,
          contentType: doc.contentType,
          componentId: doc.componentId,
          componentType: doc.componentType,
          tarot: doc.tarot,
        }
      }

      // Skip non-contentLink documents without a valid thumbnail
      if (!renderUrl) {
        return null
      }

      return {
        contentId: doc.contentId,
        thumb: renderUrl,
        title: doc.title,
        path: doc.path,
        contentType: doc.contentType,
        parentSeriesName: doc.content?.contentSeries?.[0]?.title || null,
        parentSeriesId: doc.content?.contentSeries?.[0]?.contentId || null,
        mediaId,
        mediaLanguage: pickMediaLanguage(doc.content?.featureMedia?.mediaAssets, language),
        isFeaturedMedia: !!doc.content?.featureMedia,
        componentId: doc.componentId,
        componentType: doc.componentType,
      }
    })
    .filter((doc): doc is NonNullable<typeof doc> => doc !== null)
}

interface UseOracleChatSubscriptionParams {
  /**
   * ID of the active personality
   */
  personalityId: number

  /**
   * Current chat session ID (null for new chats)
   */
  currentSessionId: string | null

  /**
   * Whether this is a new session (no existing messages)
   */
  isNewSession: boolean

  /**
   * Chat mode: 'text' or 'voice'
   */
  mode: 'text' | 'voice'

  /**
   * Skip the subscription (e.g., when in voice mode for text subscription)
   */
  skip?: boolean

  /**
   * JWT token for authentication
   */
  jwt: string

  /**
   * Whether the app is running in webview mode
   */
  isWebView: boolean

  /**
   * User timezone for rate limiting
   */
  userTimezone: string

  /**
   * Whether rate limiting is applicable for this user
   */
  isRateLimitApplicable: boolean

  /**
   * User language for content queries (default: "en")
   */
  language?: string

  /**
   * Callback when a new chat session ID is received from the subscription
   */
  onSessionReceived?: (sessionId: string, title?: string | null) => void

  /**
   * Callback when streaming completes
   */
  onComplete?: (data: {
    messagePairId: string
    chatSessionId: string
    sessionTitle?: string | null
    clientSignals?: OracleAgentChatPayload['clientSignals']
  }) => void

  /**
   * Callback when an error occurs
   */
  onError?: (error: Error) => void

  /**
   * Callback for GA4 event tracking
   */
  onMessageGa4Events?: (data: {
    success: boolean
    error: string
    messagePairId: string
    responseLength: number
  }) => void

  /**
   * Callback when AI recommendation is received (documents.size > 0)
   */
  onRecommendationReceived?: () => void

  /**
   * Chat channel derived from the current session (e.g., 'oracle_cards').
   * Parent-computed and session-scoped — ensures the channel is always
   * in sync with the active session rather than persisting across sessions.
   */
  chatChannel?: string | null

  /**
   * Callback when a moderation error is detected
   */
  onModerationError?: (categories: string[], severity: ModerationSeverity) => void

  /**
   * Callback when a divination document (tarot or oracle card) is received
   */
  onDivinationDocument?: (data: { type: 'oracle' | 'tarot'; readingId?: string | null }) => void
}

interface UseOracleChatSubscriptionReturn {
  /**
   * Accumulated streaming text (accumulated from subscription textChunk field)
   */
  streamingText: string

  /**
   * Whether the subscription is actively streaming
   */
  isStreaming: boolean

  /**
   * The user's message that is being responded to
   */
  pendingMessage: string

  /**
   * Session title received from the backend during streaming
   */
  sessionTitle: OracleAgentChatPayload['sessionTitle']

  /**
   * Rate limit state from subscription (text and voice)
   */
  subscriptionRateLimitState: SubRateLimitState

  /**
   * Documents array from the GraphQL subscription (video content metadata)
   * Type is derived from the generated subscription type
   */
  documents: SubscriptionDocuments | null

  /**
   * The normalized chat channel returned by the backend (e.g., 'oracle_cards').
   * Available after the first subscription chunk arrives.
   */
  chatChannel: string | null

  /**
   * Send a new chat message and start streaming.
   * Optional channelOverride bypasses the hook-level chatChannel (useful when
   * the URL has just been updated but React hasn't re-rendered yet).
   */
  sendMessage: (message: string, channelOverride?: string, personalityIdOverride?: string) => void

  /**
   * Stop the current streaming operation
   */
  stopStreaming: () => void

  /**
   * Error from the subscription
   */
  error: Error | null

  /**
   * Moderation error from content policy violation
   */
  moderationError: ModerationError | null

  /**
   * Clear the current moderation error
   */
  clearModerationError: () => void

  /**
   * Reset rate limit state (e.g., at midnight)
   */
  resetRateLimitState: () => void
}

/**
 * Hook to manage Oracle chat GraphQL subscription for streaming responses.
 *
 * This hook handles:
 * - Progressive text chunk accumulation from streaming responses
 * - Session management for new and existing chats
 * - Rate limit state updates from subscription payloads
 * - Apollo cache synchronization when streaming completes
 * - Cleanup when personality or session changes
 *
 * @example
 * ```typescript
 * const {
 *   streamingText,
 *   isStreaming,
 *   sendMessage,
 *   stopStreaming,
 * } = useOracleChatSubscription({
 *   personalityId: 1,
 *   currentSessionId: null,
 *   isNewSession: true,
 *   mode: 'text',
 *   jwt: authToken,
 *   isWebView: false,
 *   userTimezone: 'America/Los_Angeles',
 *   isRateLimitApplicable: true,
 *   onSessionReceived: (sessionId) => console.log('New session:', sessionId),
 * })
 * ```
 */
export function useOracleChatSubscription ({
  personalityId,
  currentSessionId,
  isNewSession,
  mode,
  skip = false,
  jwt,
  isWebView,
  userTimezone,
  isRateLimitApplicable,
  language = 'en',
  onSessionReceived,
  onComplete,
  onError,
  onMessageGa4Events,
  onRecommendationReceived,
  onModerationError,
  onDivinationDocument,
  chatChannel = null,
}: UseOracleChatSubscriptionParams): UseOracleChatSubscriptionReturn {
  const apolloClient = useApolloClient()

  // Shared moderation error handling
  const {
    moderationError,
    handleErrorInfo: handleModerationErrorInfo,
    clearModerationError,
  } = useModerationError()

  // Subscription state
  const [subscriptionInput, setSubscriptionInput] = useState<OracleAgentChatInput | null>(null)
  const [chatStreamingText, setChatStreamingText] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [pendingMessage, setPendingMessage] = useState('')
  const [streamChatSessionTitle, setStreamChatSessionTitle] = useState<string | null>(null)
  const [subscriptionRateLimitState, setSubscriptionRateLimitState] = useState<SubRateLimitState>({
    text: null,
    voice: null,
  })
  const [error, setError] = useState<Error | null>(null)
  const [documents, setDocuments] = useState<SubscriptionDocuments | null>(null)
  const [subscriptionChatChannel, setSubscriptionChatChannel] = useState<string | null>(null)

  // Stable refs for caller-provided callbacks so they don't destabilize
  // useEffect dependency arrays (callers typically pass inline arrows).
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const onMessageGa4EventsRef = useRef(onMessageGa4Events)
  onMessageGa4EventsRef.current = onMessageGa4Events
  const onRecommendationReceivedRef = useRef(onRecommendationReceived)
  onRecommendationReceivedRef.current = onRecommendationReceived
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError
  const onSessionReceivedRef = useRef(onSessionReceived)
  onSessionReceivedRef.current = onSessionReceived
  const onModerationErrorRef = useRef(onModerationError)
  onModerationErrorRef.current = onModerationError
  const onDivinationDocumentRef = useRef(onDivinationDocument)
  onDivinationDocumentRef.current = onDivinationDocument

  // Reset subscription channel when session changes
  useEffect(() => {
    setSubscriptionChatChannel(null)
  }, [currentSessionId])

  // Ref to store the first documents chunk (source of truth for document data).
  // The second chunk only identifies which componentIds the LLM referenced;
  // its data is ignored in favor of the corresponding entries from this ref.
  const masterDocumentsRef = useRef<SubscriptionDocuments | null>(null)

  // Ref to track active personality ID for cleanup
  const activePersonalityIdRef = useRef(personalityId)

  // Ref to track if subscription completed successfully
  const completedSuccessfullyRef = useRef(false)

  // Ref to track the last processed messagePairId to prevent duplicate processing
  const lastProcessedMessagePairIdRef = useRef<string | null>(null)

  // Ref to track the last messagePairId that triggered recommendation received event
  const lastRecommendationReceivedMessagePairIdRef = useRef<string | null>(null)

  // Update ref when personality changes
  useEffect(() => {
    activePersonalityIdRef.current = personalityId
  }, [personalityId])

  // Use Apollo subscription with direct text accumulation
  const subscriptionResult = useOracleAgentChatSubscriptionSubscription({
    variables: {
      input: subscriptionInput!,
      language,
    },
    skip: !subscriptionInput || skip,
    context: { jwt },
    onData: useCallback(
      ({ data: subscriptionData }) => {
        // Accumulate text directly in React state
        const textChunk = subscriptionData?.data?.oracleAgentChat?.textChunk || ''
        setChatStreamingText((prev) => prev + textChunk)

        // Handle chat session ID and channel immediately when received
        const oracleAgentChat = subscriptionData?.data?.oracleAgentChat
        if (oracleAgentChat?.chatChannel) {
          setSubscriptionChatChannel(oracleAgentChat.chatChannel)
        }
        if (oracleAgentChat?.chatSessionId && onSessionReceivedRef.current) {
          onSessionReceivedRef.current(oracleAgentChat.chatSessionId, streamChatSessionTitle)
        }

        // Handle two-chunk document delivery:
        // Chunk 1 (isFinal: false) = all RAG documents, source of truth for data
        // Chunk 2 (isFinal: true) = subset the LLM referenced, determines what to display
        if (oracleAgentChat?.documents && oracleAgentChat.documents.length > 0) {
          if (!masterDocumentsRef.current) {
            // First chunk: store as master data and expose immediately
            masterDocumentsRef.current = oracleAgentChat.documents
            setDocuments(oracleAgentChat.documents)
          } else {
            // Second chunk: use componentIds to filter master data
            const referencedIds = new Set(
              oracleAgentChat.documents
                .map((doc) => doc.componentId)
                .filter(Boolean),
            )

            if (referencedIds.size > 0) {
              // Enriched markdown: filter master docs by the IDs the LLM referenced
              const filtered = masterDocumentsRef.current.filter(
                (doc) => doc.componentId && referencedIds.has(doc.componentId),
              )
              setDocuments(filtered)
            } else {
              // Legacy (no componentIds): use final payload as the display list
              setDocuments(oracleAgentChat.documents)
            }
          }
        }
      },
      [streamChatSessionTitle],
    ),
    onComplete: useCallback(() => {
      // Mark that subscription completed successfully
      // This prevents false positive errors when connection closes after isFinal
      completedSuccessfullyRef.current = true
    }, []),
  })

  const { data: subscriptionData, error: subscriptionError } = subscriptionResult

  // Handle streaming data updates
  useEffect(() => {
    if (subscriptionData?.oracleAgentChat) {
      const { oracleAgentChat } = subscriptionData

      // Handle chat session title from stream
      if (oracleAgentChat.sessionTitle && oracleAgentChat.sessionTitle !== streamChatSessionTitle) {
        setStreamChatSessionTitle(oracleAgentChat.sessionTitle)
      }

      // Handle final response
      if (oracleAgentChat.isFinal) {
        const { messagePairId } = oracleAgentChat

        // Skip if this messagePairId has already been processed
        if (messagePairId && lastProcessedMessagePairIdRef.current === messagePairId) {
          return
        }

        // Mark this messagePairId as processed
        if (messagePairId) {
          lastProcessedMessagePairIdRef.current = messagePairId
        }

        // Note: setIsStreaming(false) is called in setTimeout below
        // to keep pending UI visible until documents can be displayed

        // Check for structured errors in errorInfo
        if (oracleAgentChat.errorInfo && oracleAgentChat.errorInfo.length > 0) {
          // First check if this is a moderation error (content policy violation)
          if (handleModerationErrorInfo(oracleAgentChat.errorInfo, onModerationErrorRef.current)) {
            // Was a moderation error - keep pendingMessage visible so user sees their message
            // above the moderation response. Stop streaming but don't clear pendingMessage.
            setIsStreaming(false)
            setChatStreamingText('')
            return
          }

          // Not a moderation error - handle as regular error
          const structuredError = oracleAgentChat.errorInfo[0]
          const errorMessage = `${structuredError.code}: ${structuredError.message}`
          setError(new Error(errorMessage))
          if (onErrorRef.current) {
            onErrorRef.current(new Error(errorMessage))
          }
          // Clear pending state immediately on error
          setIsStreaming(false)
          setPendingMessage('')
          setChatStreamingText('')
          return
        }

        // Extract rate limit status from subscription payload
        if (isRateLimitApplicable && oracleAgentChat.rateLimitStatus) {
          const { rateLimitStatus } = oracleAgentChat

          // Update subscription state based on current mode
          setSubscriptionRateLimitState((prev) => ({
            ...prev,
            [mode]: {
              remainingInteractions: rateLimitStatus.remainingInteractions,
              rateLimited: rateLimitStatus.rateLimited,
              totalInteractions: rateLimitStatus.totalInteractions,
            },
          }))
        }

        // Detect divination documents and notify consumer
        if (onDivinationDocumentRef.current && oracleAgentChat.documents) {
          const callback = onDivinationDocumentRef.current
          oracleAgentChat.documents.forEach((doc) => {
            if ((doc as { tarot?: { readingId?: string } }).tarot) {
              callback({
                type: 'tarot',
                readingId: (doc as { tarot: { readingId?: string } }).tarot.readingId || null,
              })
            } else if ((doc as { oracle?: unknown }).oracle) {
              callback({ type: 'oracle' })
            }
          })
        }

        // Create message pair from stream data
        const hasOracleDocuments = oracleAgentChat.documents?.some(
          (d: { oracle?: unknown }) => !!d.oracle,
        )
        if (pendingMessage && (chatStreamingText || hasOracleDocuments)) {
          const finalContent = chatStreamingText || ''
          const messagePair = {
            id: oracleAgentChat.messagePairId,
            userMessage: {
              content: pendingMessage,
              role: 'user',
            },
            oracleMessage: {
              content: finalContent,
              role: 'assistant',
            },
            createdAt: new Date().toISOString(),
            thumbsValue: 'none',
            mode: subscriptionInput?.mode || 'text',
            documents: transformSubscriptionDocumentsForCache(
              // Use master documents (first chunk) filtered by final chunk's componentIds.
              // The first chunk is the source of truth for document data.
              (() => {
                if (!masterDocumentsRef.current || !oracleAgentChat.documents) {
                  return oracleAgentChat.documents
                }
                const finalIds = new Set(
                  oracleAgentChat.documents.map((d) => d.componentId).filter(Boolean),
                )
                // Legacy (no componentIds): use final payload as-is
                if (finalIds.size === 0) {
                  return oracleAgentChat.documents
                }
                return masterDocumentsRef.current.filter(
                  (d) => d.componentId && finalIds.has(d.componentId),
                )
              })(),
            ),
          }

          // Update Apollo cache with the new message pair and chat session
          if (oracleAgentChat.chatSessionId) {
            try {
              // Read existing messages from cache
              const existingData = apolloClient.cache.readQuery({
                query: GetChatMessagesDocument,
                variables: {
                  chatSessionId: oracleAgentChat.chatSessionId,
                  limit: 10,
                  offset: 0,
                  order: [['createdAt', 'DESC']],
                  language,
                },
              }) as {
                oracleMessagePairList?: {
                  oracleMessagePairs: Array<{
                    userMessage?: { content: string }
                    oracleMessage?: { content: string }
                  }>
                }
              }

              if (existingData?.oracleMessagePairList) {
                const existingMessages = existingData.oracleMessagePairList.oracleMessagePairs

                // Check if this message pair already exists (by checking content similarity)
                const existingMessageIndex = existingMessages.findIndex(
                  (existingMsg: {
                    userMessage?: { content: string }
                    oracleMessage?: { content: string }
                  }) => {
                    return (
                      existingMsg.userMessage?.content === messagePair.userMessage.content
                      && existingMsg.oracleMessage?.content === messagePair.oracleMessage.content
                    )
                  },
                )

                let updatedMessages: typeof existingMessages
                if (existingMessageIndex >= 0) {
                  // Update existing message pair
                  updatedMessages = [...existingMessages]
                  updatedMessages[existingMessageIndex] = messagePair
                } else {
                  // Add new message pair at the beginning (since cache is in DESC order)
                  updatedMessages = [messagePair, ...existingMessages]
                }

                // Write updated data with merged message pairs
                apolloClient.cache.writeQuery({
                  query: GetChatMessagesDocument,
                  variables: {
                    chatSessionId: oracleAgentChat.chatSessionId,
                    limit: 10,
                    offset: 0,
                    order: [['createdAt', 'DESC']],
                    language,
                  },
                  data: {
                    oracleMessagePairList: {
                      ...existingData.oracleMessagePairList,
                      oracleMessagePairs: updatedMessages,
                    },
                  },
                })
              }

              // Update chat session with streamed title if available
              if (oracleAgentChat.sessionTitle) {
                apolloClient.cache.modify({
                  id: apolloClient.cache.identify({
                    __typename: 'OracleChatSession',
                    id: oracleAgentChat.chatSessionId,
                  }),
                  fields: {
                    name: () => oracleAgentChat.sessionTitle,
                    updatedAt: () => new Date().toISOString(),
                  },
                })

                // Also update the chat session in the history list
                try {
                  const historyQuery = apolloClient.cache.readQuery({
                    query: GetHistoryDocument,
                    variables: {
                      limit: 100,
                      offset: 0,
                      favorite: false,
                      order: [['activityUpdatedAt', 'DESC']],
                    },
                  }) as { oracleChatSessionList?: { oracleChatSessions: Array<{ id: string }> } }

                  if (historyQuery?.oracleChatSessionList) {
                    const existingSession = historyQuery
                      .oracleChatSessionList
                      .oracleChatSessions
                      .find(
                        (session: { id: string }) => session.id === oracleAgentChat.chatSessionId,
                      )

                    if (existingSession) {
                      // Update existing session in the list
                      apolloClient.cache.modify({
                        id: apolloClient.cache.identify({
                          __typename: 'OracleChatSession',
                          id: oracleAgentChat.chatSessionId,
                        }),
                        fields: {
                          name: () => oracleAgentChat.sessionTitle,
                          updatedAt: () => new Date().toISOString(),
                        },
                      })
                    } else {
                      // Add new session to the list if it doesn't exist.
                      // contentId/featureMediaId are nulled rather than omitted because
                      // GetHistory selects them; omitting triggers Apollo "missing field"
                      // warnings on every writeQuery for a brand-new session.
                      const newSession = {
                        __typename: 'OracleChatSession',
                        id: oracleAgentChat.chatSessionId,
                        name: oracleAgentChat.sessionTitle,
                        personalityId,
                        favorite: false,
                        favoriteUpdatedAt: null,
                        updatedAt: new Date().toISOString(),
                        contentId: null,
                        featureMediaId: null,
                      }

                      apolloClient.cache.writeQuery({
                        query: GetHistoryDocument,
                        variables: {
                          limit: 100,
                          offset: 0,
                          favorite: false,
                          order: [['activityUpdatedAt', 'DESC']],
                        },
                        data: {
                          oracleChatSessionList: {
                            ...historyQuery.oracleChatSessionList,
                            oracleChatSessions: [
                              newSession,
                              ...historyQuery.oracleChatSessionList.oracleChatSessions,
                            ],
                          },
                        },
                      })
                    }
                  }
                } catch {
                  // If history update fails, it's not critical - the session will appear after
                  // refetch
                }
              }
            } catch {
              // If cache read/write fails, the message will still be in React state
            }
          }

          // Delay clearing pending state AND isStreaming to allow documents to be visible
          // This gives React time to render the final message with documents
          // before the pending UI is removed
          setTimeout(() => {
            setIsStreaming(false) // Moved here from above
            setPendingMessage('')
            setChatStreamingText('')
          }, 100) // Small delay to let documents render

          // Notify completion
          if (onCompleteRef.current && oracleAgentChat.chatSessionId) {
            onCompleteRef.current({
              messagePairId: oracleAgentChat.messagePairId,
              chatSessionId: oracleAgentChat.chatSessionId,
              sessionTitle: oracleAgentChat.sessionTitle,
              clientSignals: oracleAgentChat.clientSignals,
            })
          }
        } else {
          // No-inference path: server sent isFinal with no text (e.g., oracle_begin).
          // Still need to clear streaming state so the UI doesn't hang.
          setIsStreaming(false)
          setPendingMessage('')
          setChatStreamingText('')

          if (onCompleteRef.current && oracleAgentChat.chatSessionId) {
            onCompleteRef.current({
              messagePairId: oracleAgentChat.messagePairId,
              chatSessionId: oracleAgentChat.chatSessionId,
              sessionTitle: oracleAgentChat.sessionTitle,
              clientSignals: oracleAgentChat.clientSignals,
            })
          }
        }

        // Handle GA4 events (only once per messagePairId)
        if (onMessageGa4EventsRef.current && messagePairId) {
          // GA4 events are handled as part of the final processing, so we only need
          // to check if this is the same messagePairId we're processing
          onMessageGa4EventsRef.current({
            success: oracleAgentChat.success,
            error: oracleAgentChat.error,
            messagePairId,
            responseLength: (chatStreamingText || '').length,
          })
        }

        // Track AI recommendation received event (only once per messagePairId)
        if (
          onRecommendationReceivedRef.current
          && messagePairId
          && oracleAgentChat.documents
          && oracleAgentChat.documents.length > 0
          && lastRecommendationReceivedMessagePairIdRef.current !== messagePairId
        ) {
          lastRecommendationReceivedMessagePairIdRef.current = messagePairId
          onRecommendationReceivedRef.current()
        }
      }
    }
  }, [
    subscriptionData,
    mode,
    isRateLimitApplicable,
    subscriptionInput,
    personalityId,
    apolloClient,
    streamChatSessionTitle,
  ])

  // Handle subscription errors
  useEffect(() => {
    if (subscriptionError) {
      // Only treat as error if subscription didn't complete successfully
      // This prevents false positives when connection closes after isFinal
      if (!completedSuccessfullyRef.current) {
        setIsStreaming(false)
        setPendingMessage('')
        setError(subscriptionError as Error)
        if (onErrorRef.current) {
          onErrorRef.current(subscriptionError as Error)
        }
      }
    }
  }, [subscriptionError])

  // Cleanup subscription when switching personalities
  useEffect(() => {
    // Only cleanup if we're not currently streaming and have an active subscription
    // AND the personality has actually changed
    if (
      !isStreaming
      && subscriptionInput
      && subscriptionInput.personalityId !== personalityId.toString()
    ) {
      setSubscriptionInput(null)
      setChatStreamingText('')
      setPendingMessage('')
      // Reset processed message tracking when switching personalities
      lastProcessedMessagePairIdRef.current = null
      // Reset recommendation received tracking when switching personalities
      lastRecommendationReceivedMessagePairIdRef.current = null
    }
  }, [personalityId, isStreaming, subscriptionInput])

  // Send a new chat message
  const sendMessage = useCallback(
    (message: string, channelOverride?: string, personalityIdOverride?: string) => {
      // Clear stream state for new chats
      if (isNewSession) {
        setStreamChatSessionTitle(null)
      }

      setPendingMessage(message)
      setIsStreaming(true)
      setChatStreamingText('') // Reset accumulated text for new message
      setDocuments(null) // Clear previous documents
      masterDocumentsRef.current = null // Reset master documents for new message
      setError(null) // Clear previous errors
      clearModerationError() // Clear previous moderation errors
      completedSuccessfullyRef.current = false // Reset completion flag for new message
      // Reset processed message tracking for new message
      lastProcessedMessagePairIdRef.current = null
      // Reset recommendation received tracking for new message
      lastRecommendationReceivedMessagePairIdRef.current = null

      // Clear previous subscription before starting new one
      setSubscriptionInput(null)

      // Set up the input for the subscription
      const chatInput = createOracleSubscriptionInput(
        message,
        (personalityIdOverride ? Number(personalityIdOverride) : personalityId),
        currentSessionId || null,
        mode === 'voice' ? CHAT_MODE.VOICE : CHAT_MODE.TEXT,
        isWebView,
        userTimezone,
        language,
        channelOverride || chatChannel,
      )

      // Set the new subscription input
      setSubscriptionInput(chatInput)
    },
    [
      personalityId, currentSessionId, isNewSession, mode, isWebView, userTimezone,
      clearModerationError, language, chatChannel,
    ],
  )

  // Reset rate limit state (e.g., at midnight)
  const resetRateLimitState = useCallback(() => {
    setSubscriptionRateLimitState({ text: null, voice: null })
  }, [])

  // Stop streaming
  const stopStreaming = useCallback(() => {
    setIsStreaming(false)
    setSubscriptionInput(null)
    setPendingMessage('')
    setChatStreamingText('')
  }, [])

  return {
    streamingText: chatStreamingText,
    isStreaming,
    pendingMessage,
    sessionTitle: streamChatSessionTitle,
    subscriptionRateLimitState,
    documents,
    chatChannel: subscriptionChatChannel,
    sendMessage,
    stopStreaming,
    error,
    moderationError,
    clearModerationError,
    resetRateLimitState,
  }
}
